import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { auth, db } from '../firebase';
import './AdminPage.css';

function AdminPage() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'applications'));
      const apps = [];
      querySnapshot.forEach((doc) => {
        apps.push({ id: doc.id, ...doc.data() });
      });
      
      // Sort by most recent first
      apps.sort((a, b) => {
        if (a.createdAt && b.createdAt) {
          return b.createdAt.toMillis() - a.createdAt.toMillis();
        }
        return 0;
      });
      
      setApplications(apps);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching applications:', error);
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (applicationId, newStatus) => {
    try {
      await updateDoc(doc(db, 'applications', applicationId), {
        status: newStatus
      });
      fetchApplications();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Error updating application status.');
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const filteredApplications = applications.filter(app => {
    if (filter === 'all') return true;
    return app.status === filter;
  });

  if (loading) {
    return (
      <div className="admin-page">
        <div className="loading">Loading applications...</div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <nav className="admin-nav">
        <div className="nav-container">
          <div className="logo">BetaBuyers Admin</div>
          <div className="nav-buttons">
            <button onClick={() => navigate('/admin-dashboard')} className="btn-nav">Dashboard</button>
            <button onClick={() => navigate('/products')} className="btn-nav">Products</button>
            <button onClick={handleLogout} className="btn-logout">Sign Out</button>
          </div>
        </div>
      </nav>

      <div className="admin-content">
        <div className="admin-header">
          <h1>All Applications</h1>
          <p>Review and manage beta tester applications</p>
        </div>

        <div className="filter-section">
          <button 
            className={`btn-filter ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All ({applications.length})
          </button>
          <button 
            className={`btn-filter ${filter === 'pending' ? 'active' : ''}`}
            onClick={() => setFilter('pending')}
          >
            Pending ({applications.filter(a => a.status === 'pending').length})
          </button>
          <button 
            className={`btn-filter ${filter === 'approved' ? 'active' : ''}`}
            onClick={() => setFilter('approved')}
          >
            Approved ({applications.filter(a => a.status === 'approved').length})
          </button>
          <button 
            className={`btn-filter ${filter === 'rejected' ? 'active' : ''}`}
            onClick={() => setFilter('rejected')}
          >
            Rejected ({applications.filter(a => a.status === 'rejected').length})
          </button>
        </div>

        <div className="applications-list">
          {filteredApplications.length === 0 ? (
            <p className="no-applications">No applications found</p>
          ) : (
            filteredApplications.map(app => (
              <div key={app.id} className="application-card">
                <div className="application-header">
                  <div className="applicant-info">
                    <h3>{app.firstName} {app.lastName}</h3>
                    <p className="applicant-email">{app.email || app.userEmail}</p>
                    <div className="product-badge">
                      <span className="badge-label">Product:</span>
                      <span className="badge-value">{app.productName}</span>
                    </div>
                  </div>
                  <span className={`status-badge ${app.status}`}>
                    {app.status?.charAt(0).toUpperCase() + app.status?.slice(1)}
                  </span>
                </div>

                <div className="application-details">
                  <div className="details-row">
                    <div className="detail-col">
                      <h4>Personal Information</h4>
                      <div className="info-pair">
                        <span className="info-label">Age:</span>
                        <span className="info-value">{app.age}</span>
                      </div>
                      <div className="info-pair">
                        <span className="info-label">Location:</span>
                        <span className="info-value">{app.location}</span>
                      </div>
                      <div className="info-pair">
                        <span className="info-label">Occupation:</span>
                        <span className="info-value">{app.occupation || 'Not provided'}</span>
                      </div>
                      <div className="info-pair">
                        <span className="info-label">Social Media:</span>
                        <span className="info-value">{app.socialMedia || 'Not provided'}</span>
                      </div>
                    </div>

                    <div className="detail-col">
                      <h4>Contact Information</h4>
                      <div className="info-pair">
                        <span className="info-label">Email:</span>
                        <span className="info-value">{app.email}</span>
                      </div>
                      <div className="info-pair">
                        <span className="info-label">Phone:</span>
                        <span className="info-value">{app.phoneNumber}</span>
                      </div>
                      <div className="info-pair">
                        <span className="info-label">NDA Agreed:</span>
                        <span className={`info-value ${app.ndaAgreed ? 'success' : 'warning'}`}>
                          {app.ndaAgreed ? 'Yes ✓' : 'No ✗'}
                        </span>
                      </div>
                      <div className="info-pair">
                        <span className="info-label">Marketing:</span>
                        <span className="info-value">{app.marketingConsent ? 'Yes ✓' : 'No ✗'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="detail-full">
                    <h4>Previous Beta Testing Experience</h4>
                    <p>{app.previousExperience}</p>
                  </div>

                  <div className="detail-full">
                    <h4>Specific Interests</h4>
                    <p>{app.specificInterests}</p>
                  </div>

                  <div className="detail-full">
                    <h4>Why do you want to test this product?</h4>
                    <p>{app.reason}</p>
                  </div>

                  <div className="detail-full">
                    <h4>Experience with Tech Products</h4>
                    <p>{app.experience}</p>
                  </div>

                  <div className="submission-info">
                    <span className="submission-label">Submitted:</span>
                    <span className="submission-date">
                      {app.createdAt ? new Date(app.createdAt.toMillis()).toLocaleString() : 'N/A'}
                    </span>
                  </div>
                </div>

                <div className="application-actions">
                  <button 
                    onClick={() => handleStatusUpdate(app.id, 'approved')}
                    className="btn-approve"
                    disabled={app.status === 'approved'}
                  >
                    ✓ Approve
                  </button>
                  <button 
                    onClick={() => handleStatusUpdate(app.id, 'rejected')}
                    className="btn-reject"
                    disabled={app.status === 'rejected'}
                  >
                    ✗ Reject
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminPage;